import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { StructuredDataView } from '@/components/common/StructuredDataView';
import type { ExecutionLogEntry, StageDetail } from '@/api/cockpit';
import { formatPipelineStepLabel, formatRelativeTime, formatWorkflowStateLabel } from '@/lib/utils';
import type { TimelineSubstepSelection } from '@/components/common/StateMachineTimeline';

export function StageDetailDialog({
  detail,
  open,
  onOpenChange,
  selectedSubstep,
}: {
  detail: StageDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedSubstep?: TimelineSubstepSelection | null;
}): JSX.Element {
  const filteredInternalSteps =
    detail && selectedSubstep
      ? detail.internalSteps.filter(
          (step, index) => (step.id ?? `internal-${index}`) === selectedSubstep.id,
        )
      : (detail?.internalSteps ?? []);
  const filteredArtifacts =
    detail && selectedSubstep
      ? detail.artifacts.filter(
          (artifact) => artifact.substepId == null || artifact.substepId === selectedSubstep.id,
        )
      : (detail?.artifacts ?? []);
  const filteredLlmCalls =
    detail && selectedSubstep
      ? detail.llmCalls.filter(
          (call) => call.substepId == null || call.substepId === selectedSubstep.id,
        )
      : (detail?.llmCalls ?? []);
  const filteredExecutionLogs = detail?.executionLogs ?? [];
  const selectedExecutionLogs =
    detail && selectedSubstep
      ? filteredExecutionLogs.filter((entry) => entry.substepId === selectedSubstep.id)
      : filteredExecutionLogs;
  const isIsolatedView = Boolean(selectedSubstep);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-5xl overflow-hidden p-0">
        {detail ? (
          <div className="grid max-h-[88vh] grid-rows-[auto,1fr]">
            <DialogHeader className="border-b px-6 py-5">
              <DialogTitle className="flex items-center gap-2">
                <span>{detail.label}</span>
                <Badge variant="outline">{detail.status}</Badge>
                {selectedSubstep ? (
                  <Badge variant="secondary">{selectedSubstep.label}</Badge>
                ) : null}
              </DialogTitle>
              <DialogDescription>
                {isIsolatedView ? `Showing the selected detail.` : detail.summary}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 overflow-y-auto px-6 py-5">
              {!detail.unavailableReason &&
              filteredLlmCalls.length === 0 &&
              filteredArtifacts.length === 0 &&
              selectedExecutionLogs.length === 0 &&
              filteredInternalSteps.length === 0 ? (
                <section className="rounded-md border border-border/70 bg-muted/30 p-4 text-sm text-muted-foreground">
                  No details are available for this step.
                </section>
              ) : null}

              {detail.unavailableReason ? (
                <section className="rounded-md border border-border/70 bg-muted/30 p-4 text-sm text-muted-foreground">
                  {detail.unavailableReason}
                </section>
              ) : null}

              {filteredLlmCalls.length > 0 || detail.llmTraceMissingReason ? (
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold">AI Checks</h3>
                  {detail.llmTraceMissingReason ? (
                    <div className="rounded-md border border-border/70 bg-muted/30 p-4 text-sm text-muted-foreground">
                      {detail.llmTraceMissingReason}
                    </div>
                  ) : null}
                  <div className="space-y-4">
                    {filteredLlmCalls.map((call) => (
                      <div key={call.id} className="rounded-md border p-4">
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline">{call.provider}</Badge>
                          {call.substep ? (
                            <Badge variant="outline">
                              {formatPipelineStepLabel(call.substepId ?? call.substep)}
                            </Badge>
                          ) : null}
                          <span>{call.modelVersion ?? 'unknown'}</span>
                          <span>{formatRelativeTime(call.producedAt)}</span>
                        </div>
                        <TraceBlock title="Instructions" content={call.systemPrompt} />
                        <TraceBlock title="Input" content={call.userPrompt} />
                        <StructuredDataView title="Response" content={call.rawResponse} />
                        {call.parsedResponse ? (
                          <StructuredDataView title="Result" content={call.parsedResponse} />
                        ) : null}
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              {filteredArtifacts.length > 0 ? (
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold">Files and Data</h3>
                  <div className="space-y-3">
                    {filteredArtifacts.map((artifact) => (
                      <ArtifactBlock
                        key={artifact.title}
                        title={artifact.title}
                        format={artifact.format}
                        content={artifact.content}
                      />
                    ))}
                  </div>
                </section>
              ) : null}

              {selectedExecutionLogs.length > 0 ? (
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold">Activity</h3>
                  <div className="relative pl-6">
                    <div className="absolute bottom-0 left-[0.44rem] top-0 w-px bg-border" />
                    <div className="space-y-4">
                      {selectedExecutionLogs.map((entry) => (
                        <div key={entry.id} className="relative">
                          <div className="absolute left-[-1.5rem] top-3 h-3 w-3 rounded-full border-2 border-background bg-primary" />
                          <div
                            className={`rounded-md border-l-4 border-y border-r p-3 ${executionLogToneClass(entry)}`}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="text-sm font-medium">
                                {formatWorkflowStateLabel(entry.fromState)} to{' '}
                                {formatWorkflowStateLabel(entry.toState)}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatRelativeTime(entry.emittedAt)}
                              </div>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                              <span>Time: {entry.latencyMs}ms</span>
                              <span>
                                Note:{' '}
                                <span className="font-semibold text-foreground">
                                  {entry.reason ?? 'none'}
                                </span>
                              </span>
                              <span>Model: {entry.modelVersion ?? 'n/a'}</span>
                            </div>
                            {entry.detail ? (
                              <StructuredDataView
                                title="Details"
                                content={entry.detail}
                                className="mt-2"
                              />
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              ) : null}

              {filteredInternalSteps.length > 0 ? (
                <section className="space-y-3">
                  <h3 className="text-sm font-semibold">Details</h3>
                  <div className="relative pl-6">
                    <div className="absolute bottom-0 left-[0.44rem] top-0 w-px bg-border" />
                    <div className="space-y-3">
                      {filteredInternalSteps.map((step, index) => (
                        <div key={`${step.title}-${index}`} className="relative">
                          <div className="absolute left-[-1.5rem] top-3 h-3 w-3 rounded-full border-2 border-background bg-muted-foreground" />
                          <div className="rounded-md border p-3">
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={
                                  step.tone === 'danger'
                                    ? 'destructive'
                                    : step.tone === 'warning'
                                    ? 'warning'
                                    : step.tone === 'success'
                                      ? 'default'
                                      : 'outline'
                                }
                              >
                                {step.tone}
                              </Badge>
                              <div className="text-sm font-medium">{step.title}</div>
                            </div>
                            {step.detail ? (
                              <div className="mt-2 text-sm text-muted-foreground">
                                {step.detail}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              ) : null}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ArtifactBlock({
  title,
  format,
  content,
}: {
  title: string;
  format: 'text' | 'json' | 'markdown';
  content: string;
}): JSX.Element {
  if (format === 'json') {
    return <StructuredDataView title={title} content={content} />;
  }
  return <TraceBlock title={title} content={content} />;
}

function executionLogToneClass(entry: ExecutionLogEntry): string {
  if (entry.reason) {
    const normalized = entry.toState.toLowerCase();
    if (normalized.includes('failed') || normalized.includes('error')) {
      return 'border-l-red-600';
    }
    return 'border-l-yellow-500';
  }
  return 'border-l-emerald-600';
}

function TraceBlock({ title, content }: { title: string; content: string }): JSX.Element {
  return (
    <div className="mt-3 space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <pre className="max-h-56 overflow-auto rounded-md border bg-muted/20 p-3 text-xs leading-5 text-foreground whitespace-pre-wrap">
        {content}
      </pre>
    </div>
  );
}
